import _ from 'lodash';

import Trip from '../records/Trip';
import Order from '../records/Order';
import OrderStore from '../stores/OrderStore';
import UserStore from '../stores/UserStore';
import SmartcardStore from '../stores/SmartcardStore';
import LinkingSmartcardsStore from '../stores/LinkingSmartcardsStore';
import InAppStore from '../stores/InAppStore';
import ModalActions from './ModalActions';
import OrderService from '../services/OrderService';
import OrderHistoryService from '../services/OrderHistoryService';
import AppDispatcher from '../dispatcher/AppDispatcher';
import { ActionTypes, CookieKeys, GlobalModals, JourneyDirections, LoadingTypes } from '../constants/AppConstants';
import StationService from '../services/StationService';
import NotificationActions from './NotificationActions';
import TokenActions from './TokenActions';
import {
    checkOrderExpiry,
    deleteMatchingPassengerAssistRequests,
    handleOrderErrors,
    setOrderDataCookie,
} from '../decorators/Order';
import { createLoadingDecoratorFor } from '../decorators/Loading';
import DisruptionAlertActions from './DisruptionAlertActions';
import RewardPointsActions from './RewardPointsActions';
import FeatureFlagStore from '../stores/FeatureFlagStore';
import PassengerAssistActions from './PassengerAssistActions';
import CookieHelper from '../helpers/Cookie';


const withOrderLoadingActions = createLoadingDecoratorFor(LoadingTypes.ORDER);

const actions = {

    async updateAddress(order) {
        const updatedOrder = await OrderService.updateFulfilment(order);

        AppDispatcher.dispatch({
            order: updatedOrder,
            actionType: ActionTypes.ORDER_UPDATED
        });

        return updatedOrder;
    },

    @checkOrderExpiry
    checkBasket () {
        // returns TRUE only if @checkOrderExpiry allows this action to run
        return true;
    },

    @withOrderLoadingActions
    @handleOrderErrors
    @checkOrderExpiry
    @setOrderDataCookie
    async create ({ trip, order }) {
        if (!order) { order = new Order(); }

        this.handleOrderLoading(true);

        AppDispatcher.dispatch({
            trip,
            actionType: ActionTypes.ORDER_STARTED
        });

        try {
            const channel = InAppStore.isInApp() ? InAppStore.getChannel() : 'web';

            const { order: createdOrder, deviceToken } = await OrderService.create(order, trip, channel);

            AppDispatcher.dispatch({
                order: createdOrder,
                trip: trip.set('orderTripId', createdOrder.trips.first().id),
                actionType: ActionTypes.ORDER_CREATED
            });

            TokenActions.updateDevice(deviceToken);

            if (FeatureFlagStore.get('enablePassengerAssist')) {
                const mostRecentAddedTrip = createdOrder.trips.last();
                PassengerAssistActions.addOrderTripIdToMostRecentPassengerAssistRequest(mostRecentAddedTrip.id);
            }

            NotificationActions.showNotification('notifications.basket.itemAdded');

            return createdOrder;
        } catch (error) {
            AppDispatcher.dispatch({
                actionType: ActionTypes.ORDER_FAILED,
                error
            });
            throw error;
        } finally {
            this.handleOrderLoading(false);
        }
    },

    @withOrderLoadingActions
    @handleOrderErrors
    @checkOrderExpiry
    @setOrderDataCookie
    async addTrip (order, trip) {
        let updatedOrder = order;

        this.handleOrderLoading(true);

        try {
            updatedOrder = await OrderService.addTrip(order, trip);
        } catch (error) {
            if (InAppStore.isInApp()) {
                // InApp crashes when user adds another trip to a non-empty basket.
                // It does not properly clears the basket after logging in/out or when
                // user abandoned the basket that results in invalid X-Customer-Device token.
                // In that case catch the error, clear the basket and create a new order
                // with the currently selected trip.
                this.clearBasket(order);
                updatedOrder = await this.create({ trip });
            } else {
                throw error;
            }
        } finally {
            this.handleOrderLoading(false);
        }

        const knownIds = order.getTripIds();
        const orderTrip = updatedOrder.trips.find((t) => !knownIds.includes(t.getCleanId()));

        AppDispatcher.dispatch({
            orderTrip,
            order: updatedOrder,
            trip: trip.set('orderTripId', orderTrip.id),
            actionType: ActionTypes.ORDER_TRIP_CREATED
        });

        if (FeatureFlagStore.get('enablePassengerAssist')) {
            const mostRecentAddedTrip = updatedOrder.trips.last();
            PassengerAssistActions.addOrderTripIdToMostRecentPassengerAssistRequest(mostRecentAddedTrip.id);
        }

        NotificationActions.showNotification('notifications.basket.itemAdded');

        return updatedOrder;
    },

    @withOrderLoadingActions
    @handleOrderErrors
    @checkOrderExpiry
    @setOrderDataCookie
    @deleteMatchingPassengerAssistRequests
    async deleteTrip (order, orderTrip) {
        await OrderService.deleteTrip(orderTrip);
        const tripDeletedOrder = await OrderService.get(order.id);
        AppDispatcher.dispatch({
            order: tripDeletedOrder,
            orderTrip,
            actionType: ActionTypes.ORDER_TRIP_DELETED
        });

        NotificationActions.showNotification('notifications.basket.itemDeleted');
        return tripDeletedOrder;
    },

    @withOrderLoadingActions
    @handleOrderErrors
    @checkOrderExpiry
    @setOrderDataCookie
    async updateTrip (order, orderTrip, trip) {
        this.handleOrderLoading(true);

        try {
            await OrderService.deleteTrip(orderTrip);
            const tripDeletedOrder = await OrderService.get(order.id);
            const tripAddedOrder = await this.addTrip(tripDeletedOrder, trip);
            return tripAddedOrder;
        } catch (e) {
            throw e;
        } finally {
            this.handleOrderLoading(false);
        }
    },

    @checkOrderExpiry
    async checkTrip ({ trip, smartcard }) {
        const order = OrderStore.getCurrent();
        const isSmartcardTrip = trip.isSmartcard();
        const isSmartcardOrder = order.isSmartcard();

        if (order.trips.size && (isSmartcardTrip || isSmartcardOrder)) {
            ModalActions.open(GlobalModals.BASKET);
            return false;
        }

        if (isSmartcardTrip) {
            const user = UserStore.getCurrent();
            if (!user.isLoggedIn()) {
                ModalActions.open(GlobalModals.LOGIN);
                return false;
            }

            if (!smartcard || !smartcard.canLoadProduct()) {
                ModalActions.open(GlobalModals.ERROR, { message: 'booking.error.noSmartcard'});
                return false;
            }
        }

        return { trip };
    },

    @handleOrderErrors
    @checkOrderExpiry
    @setOrderDataCookie
    createOrUpdate ({ order, trip }) {

        if (!order) { order = OrderStore.getCurrent(); }

        if (!order.id) {
            return this.create({ order, trip });
        }

        return this.addTrip(order, trip);
    },

    @handleOrderErrors
    @checkOrderExpiry
    @setOrderDataCookie
    async changeTripPassengers (order, trip) {
        if (!order.id) { return false; }
        const orderTrip = order.getTrip(trip.orderTripId);
        if (!orderTrip) { return false; }
        const rrFare = orderTrip.getRoversRangersFare();
        const passengers = trip.passengers.setTicketIds(rrFare.tickets);
        if (!passengers.isComplete()) {
            this.deleteTrip(order, orderTrip);
            return false;
        }

        try {
            if (rrFare.ticketType.isFamilyRanger()) {
                return this.updateTrip(order, orderTrip, trip);
            }

            await OrderService.updatePassengers(order, orderTrip, passengers);
            const updatedOrder = await OrderService.get(order.id);

            AppDispatcher.dispatch({
                order: updatedOrder,
                actionType: ActionTypes.ORDER_TRIP_UPDATED
            });

            return updatedOrder;
        } catch (e) {
            this.deleteTrip(order, orderTrip);
            return false;
        }
    },

    @handleOrderErrors
    @checkOrderExpiry
    @setOrderDataCookie
    async createOrderForPreRegisteredSmartcard({ trip }) {
        const order = await this.create({ trip });
        const deliveryOptions = await this.loadDeliveryOptions(order);

        const smartcardOption = deliveryOptions.find((option) => option.isSmartcard());

        await this.updateFulfilmentMethod(smartcardOption);
        return this.update();

    },

    @withOrderLoadingActions
    @handleOrderErrors
    @checkOrderExpiry
    @setOrderDataCookie
    async addOrUpdatePlusBus (plusBus, trip, order) {
        if (!order) { order = OrderStore.getCurrent(); }

        const tripWithPlusBus = trip.setPlusBus(plusBus);

        if (!order.id) { return this.create({ order, trip: tripWithPlusBus }); }

        const orderTrip = order.getTrip(trip.orderTripId);

        if (!orderTrip) {
            return this.addTrip(order, tripWithPlusBus);
        }

        if (plusBus !== trip.getPlusBus()) {
            const updatedOrder = await OrderService.updatePlusBus(order, orderTrip, tripWithPlusBus);

            AppDispatcher.dispatch({
                orderTrip: updatedOrder.getTrip(orderTrip.id),
                order: updatedOrder,
                trip: tripWithPlusBus,
                actionType: ActionTypes.ORDER_PLUSBUS_UPDATED
            });
        }

        return order;
    },

    @handleOrderErrors
    @checkOrderExpiry
    @setOrderDataCookie
    async update ({ order } = {}, { disableAddress = false } = {}) {
        if (!order) { order = OrderStore.getCurrent(); }
        if (!order || !_.get(order, 'delivery.fulfilmentMethod')) { return null; }

        this.handleOrderLoading(true);

        try {
            const linkedSmartcards = LinkingSmartcardsStore.getAllLinkedSmartcards();
            const updatedOrder = await OrderService.updateFulfilment(order, { disableAddress, linkedSmartcards });

            if (FeatureFlagStore.get('enableRewardPoints') && !updatedOrder.isPaid()) {
                RewardPointsActions.registerTicketsForRewardPoints(updatedOrder);
            }

            const user = UserStore.getCurrent();
            const { storedAddress } = order.delivery;
            if (user.isLoggedIn() && order.delivery.isPostalDelivery() && storedAddress) {
                this.updatePostalFulfilmentStoredAddress(storedAddress);
            }

            AppDispatcher.dispatch({
                order: updatedOrder,
                actionType: ActionTypes.ORDER_UPDATED
            });

            return updatedOrder;
        } catch (e) {
            throw e;
        } finally {
            this.handleOrderLoading(false);
        }
    },

    @setOrderDataCookie
    clearBasket (order) {
        if (order) {
            /*
             * Fire and forget delete order, so backend can cleanup and release
             * tickets with NRS (CORE-1793)
             */
            OrderService.delete(order)
                .catch(e => { console.warn(`delete order failed`, e); });
        }

        AppDispatcher.dispatch({
            actionType: ActionTypes.BASKET_CLEARED
        });

        NotificationActions.showNotification('notifications.basket.cleared');

        DisruptionAlertActions.resetTimetableAlerts();
    },

    @handleOrderErrors
    @checkOrderExpiry
    handleOrderLoading (value = false) {
        AppDispatcher.dispatch({
            isLoading: value,
            actionType: ActionTypes.ORDER_LOADING
        });
    },

    @handleOrderErrors
    @checkOrderExpiry
    handleOrderLoadingDeliveryOptions (value = false) {
        AppDispatcher.dispatch({
            isLoadingDeliveryOptions: value,
            actionType: ActionTypes.ORDER_DELIVERY_OPTIONS_LOADING
        });
    },

    @handleOrderErrors
    @checkOrderExpiry
    updateFulfilment (path, value) {
        AppDispatcher.dispatch({
            path,
            value,
            actionType: ActionTypes.FULFILMENT_UPDATED
        });
    },

    async deleteSTicketPhoto (userId) {
        try {
            await OrderService.deleteSTicketPhoto(userId);
            return true;
        } catch (err) {
            const errorMessage = _.get(err, 'body.errors.message');
            ModalActions.open(GlobalModals.ERROR, { message: errorMessage });
            return false;
        }
    },

    async updateSTicketPhoto (userId, photo) {
        return await OrderService.updateSTicketPhoto(userId, photo);
    },

    async sendSTicketPhotoChangeRequest(userId, photo) {
        const request = await OrderService.sendSTicketPhotoChangeRequest(userId, photo);

        NotificationActions.showNotification('sticket.photo.change.notification');

        return request;
    },

    async getSTicketPhoto (userId) {
        const photoBlob = await OrderService.getSTicketPhoto(userId);
        return photoBlob;
    },

    @handleOrderErrors
    @checkOrderExpiry
    updateFulfilmentMethod (deliveryOption) {
        AppDispatcher.dispatch({
            deliveryOption,
            actionType: ActionTypes.FULFILMENT_METHOD_UPDATED
        });
    },

    updatePostalFulfilmentStoredAddress (address) {
        AppDispatcher.dispatch({
            address,
            actionType: ActionTypes.POSTAL_FULFILMENT_STORED_ADDRESS_UPDATED
        });
    },

    @withOrderLoadingActions
    async load (orderID) {
        AppDispatcher.dispatch({
            orderID,
            actionType: ActionTypes.ORDER_LOAD_STARTED
        });

        try {
            const order = await OrderService.get(orderID);

            AppDispatcher.dispatch({
                order: order,
                actionType: ActionTypes.ORDER_LOADED
            });
            return order;
        } catch (error) {
            AppDispatcher.dispatch({
                orderID, error,
                actionType: ActionTypes.ORDER_LOAD_FAILED
            });
            throw error;
        }

    },

    @withOrderLoadingActions
    @checkOrderExpiry
    @setOrderDataCookie
    async refresh (order) {
        AppDispatcher.dispatch({
            order,
            actionType: ActionTypes.ORDER_REFRESH_STARTED
        });

        try {
            const refreshedOrder = await OrderService.get(order.id);

            if (refreshedOrder.isPaid()) {
                AppDispatcher.dispatch({
                    order: refreshedOrder,
                    actionType: ActionTypes.ORDER_PAID
                });
            } else {
                AppDispatcher.dispatch({
                    order: refreshedOrder,
                    actionType: ActionTypes.ORDER_REFRESH_LOADED
                });
            }
            return refreshedOrder;
        } catch (error) {
            AppDispatcher.dispatch({
                order, error,
                actionType: ActionTypes.ORDER_REFRESH_FAILED
            });
            return null;
        }
    },

    @setOrderDataCookie
    refreshOrderFromCookieData () {
        if (!FeatureFlagStore.get('enableBasketWidget')) {
            return;
        }

        const orderCookie = CookieHelper.get(CookieKeys.ORDER, true);
        const { id: orderId, journeysAmount } = orderCookie && orderCookie.order || {};
        if (!orderId) {
            return;
        }
        if (journeysAmount) {
            this.update();
        } else {
            AppDispatcher.dispatch({ actionType: ActionTypes.BASKET_CLEARED });
        }
    },

    @withOrderLoadingActions
    @checkOrderExpiry
    async refreshAmendment (order) {
        AppDispatcher.dispatch({
            order,
            actionType: ActionTypes.ORDER_REFRESH_STARTED
        });

        try {
            const refreshedOrder = await OrderHistoryService.getOrderHistory(order.id);

            if (refreshedOrder.isPaid()) {
                AppDispatcher.dispatch({
                    order: refreshedOrder,
                    actionType: ActionTypes.ORDER_PAID
                });
            } else {
                AppDispatcher.dispatch({
                    order: refreshedOrder,
                    actionType: ActionTypes.ORDER_REFRESH_LOADED
                });
            }
            return refreshedOrder;
        } catch (error) {
            AppDispatcher.dispatch({
                order, error,
                actionType: ActionTypes.ORDER_REFRESH_FAILED
            });
            return null;
        }
    },

    @withOrderLoadingActions
    @handleOrderErrors
    @checkOrderExpiry
    async loadDeliveryOptions (order = OrderStore.getCurrent(), smartcard = SmartcardStore.getCurrent()) {

        this.handleOrderLoadingDeliveryOptions(true);

        try {
            let deliveryOptions = await OrderService.getDeliveryOptions(order.id, smartcard);

            AppDispatcher.dispatch({
                order, deliveryOptions,
                actionType: ActionTypes.ORDER_DELIVERY_OPTIONS_LOADED
            });

            this.handleOrderLoadingDeliveryOptions(false);

            return deliveryOptions;
        } catch (e) {
            this.handleOrderLoadingDeliveryOptions(false);

            throw e;
        }
    },

    @withOrderLoadingActions
    @handleOrderErrors
    @checkOrderExpiry
    async createSmartCarnetOrder (trip) {
        const outwardTrip = new Trip().setFareForDirection(JourneyDirections.OUTWARD, trip.getOutwardFare());
        const inwardTrip = new Trip().setFareForDirection(JourneyDirections.OUTWARD, trip.getInwardFare());

        let order = await this.create({ order: new Order(), trip: outwardTrip });

        if (inwardTrip.hasOutwardFare()) {
            order = await this.addTrip(order, inwardTrip);
        }

        return order;
    },


    async checkTaxiBookingAvailability (order) {
        const destinationRequests = order.get('trips')
            .filter((trip) => trip.needsJourney())
            .map((trip) => trip.getDestination())
            .filter((destination) => destination.get('crs'))
            .map((destination) => StationService.search(destination.get('crs')));

        const destinationResponses = await Promise.all(destinationRequests.toArray());

        const onwardTaxiBookingAvailable = destinationResponses
            .some((response) => response.first().get('onwardTaxiBookingAvailable'));

        AppDispatcher.dispatch({
            onwardTaxiBookingAvailable,
            actionType: ActionTypes.ORDER_TAXI_BOOKING_AVAILABILITY
        });

        return onwardTaxiBookingAvailable;
    },

    async linkOrderToAccount (token, email) {
        AppDispatcher.dispatch({
            actionType: ActionTypes.LINK_ORDER_TO_ACCOUNT
        });

        return await OrderService.linkToAccount(token, email);
    },

    updateLeadPassenger (trip, leadPassengerName) {
        AppDispatcher.dispatch({
            trip, leadPassengerName,
            actionType: ActionTypes.ORDER_TRIP_LEAD_PASSENGER_UPDATED
        });
    },

    @withOrderLoadingActions
    @handleOrderErrors
    @checkOrderExpiry
    @setOrderDataCookie
    async applyDiscountCode (discountCode) {
        AppDispatcher.dispatch({
            actionType: ActionTypes.APPLY_DISCOUNT_CODE_STARTED
        });

        try {
            const updatedOrder = await OrderService.applyDiscountCode(OrderStore.getCurrent(), discountCode);

            AppDispatcher.dispatch({
                updatedOrder,
                actionType: ActionTypes.APPLY_DISCOUNT_CODE_FINISHED
            });

            return updatedOrder;
        } catch (error) {
            AppDispatcher.dispatch({
                error,
                actionType: ActionTypes.APPLY_DISCOUNT_CODE_FAILED
            });
            return null;
        }
    },

    @withOrderLoadingActions
    @handleOrderErrors
    @checkOrderExpiry
    @setOrderDataCookie
    async removeDiscountCode (discountCode) {
        AppDispatcher.dispatch({
            actionType: ActionTypes.DELETE_DISCOUNT_CODE_STARTED
        });

        try {
            const updatedOrder = await OrderService.removeDiscountCode(OrderStore.getCurrent(), discountCode);

            AppDispatcher.dispatch({
                updatedOrder,
                actionType: ActionTypes.DELETE_DISCOUNT_CODE_FINISHED
            });

            return updatedOrder;
        } catch (error) {
            AppDispatcher.dispatch({
                error,
                actionType: ActionTypes.DELETE_DISCOUNT_CODE_FAILED
            });
            return null;
        }
    },

    async lock (order) {
        try {
            if (!(order && order.id)) {
                console.error('OrderActions.lock error: Invalid order object: ', order);
                return;
            }

            await OrderService.lock(order);
            AppDispatcher.dispatch({
                order,
                actionType: ActionTypes.ORDER_LOCKED
            });
        } catch (error) {
            console.error('OrderActions.lock error: ', error);
        }
    },

    async unlock(order) {
        if (!(order && order.id)) {
            console.error('OrderActions.unlock error: Invalid order object: ', order);
            return;
        }

        try {
            await OrderService.unlock(order);
            AppDispatcher.dispatch({
                order,
                actionType: ActionTypes.ORDER_UNLOCKED,
            });
        } catch (error) {
            console.error('OrderActions.unlock error: ', error);
        }
    },

    async loadChangeovers (order, customerId) {
        return OrderService.loadChangeovers(order, customerId);
    },

    async updateContactDetails ({email, telephoneNumber}) {
        AppDispatcher.dispatch({
            email,
            telephoneNumber,
            actionType: ActionTypes.ORDER_CONTACT_DETAILS_UPDATED,
        });
    },

    updateHandoffDetails (handoffDetails) {
        AppDispatcher.dispatch({
            handoffDetails,
            actionType: ActionTypes.ORDER_HANDOFF_UPDATED,
        });
    },

    updateHandoffReferralReported (referralReported) {
        AppDispatcher.dispatch({
            referralReported,
            actionType: ActionTypes.ORDER_HANDOFF_REFERRAL_REPORTED,
        });
    },

    validateRewardCode (data) {
        return OrderService.validateRewardCode(data);
    },

    updateOrder (order) {
        _.defer(() => AppDispatcher.dispatch({
            order: order,
            actionType: ActionTypes.ORDER_UPDATED
        }));
    },

};

export default actions;



// WEBPACK FOOTER //
// ./src/actions/OrderActions.js